<nz-modal
  [nzVisible]="isVisible()"
  [nzTitle]="modalHeader"
  [nzWidth]="'960px'"
  nzClassName="rounded-2xl"
  (nzOnCancel)="close()"
  [nzFooter]="modalFooter"
>
  <!-- MODAL HEADER -->
  <ng-template #modalHeader>
    <div class="flex items-center space-x-3">
      <div class="w-8 h-8 rounded-xl bg-blue-500/10 text-primary flex items-center justify-center text-base border border-blue-500/20 shrink-0">
        <i class="fa-regular fa-circle-question"></i>
      </div>
      <div>
        <h3 class="font-extrabold text-base text-gray-900 m-0">Task Pipeline &amp; Development Guide</h3>
        <p class="text-xs text-secondary-text m-0">A step-by-step user guide to reporting bugs, requesting features, and automated AI building</p>
      </div>
    </div>
  </ng-template>

  <!-- MODAL CONTENT -->
  <ng-container *nzModalContent>
    <div class="flex flex-col gap-6 py-2 max-h-[75vh] overflow-y-auto pr-1.5 custom-scrollbar text-gray-800">

      <!-- ======================================================== -->
      <!-- TOP BANNER: USER QUICK START TIPS                        -->
      <!-- ======================================================== -->
      <div class="grid grid-cols-1 @sm:grid-cols-3 gap-3">
        <div class="p-3 bg-blue-50/60 border border-blue-200/70 rounded-xl flex items-start gap-2.5">
          <div class="w-7 h-7 rounded-lg bg-blue-100 text-blue-700 flex items-center justify-center text-xs shrink-0 mt-0.5 font-bold">
            <i class="fa-solid fa-location-dot"></i>
          </div>
          <div class="flex flex-col">
            <span class="text-xs font-bold text-blue-950">1. Pin on Canvas</span>
            <span class="text-[11px] text-gray-600 mt-0.5 leading-snug">Click "Report Bug" and drop a bubble pin directly on any UI element to show exact location.</span>
          </div>
        </div>

        <div class="p-3 bg-purple-50/60 border border-purple-200/70 rounded-xl flex items-start gap-2.5">
          <div class="w-7 h-7 rounded-lg bg-purple-100 text-purple-700 flex items-center justify-center text-xs shrink-0 mt-0.5 font-bold">
            <i class="fa-solid fa-robot"></i>
          </div>
          <div class="flex flex-col">
            <span class="text-xs font-bold text-purple-950">2. Autonomous AI Work</span>
            <span class="text-[11px] text-gray-600 mt-0.5 leading-snug">Once accepted by an admin, the AI Builder automatically writes code, executes checks, and stages fixes.</span>
          </div>
        </div>

        <div class="p-3 bg-emerald-50/60 border border-emerald-200/70 rounded-xl flex items-start gap-2.5">
          <div class="w-7 h-7 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center text-xs shrink-0 mt-0.5 font-bold">
            <i class="fa-solid fa-clipboard-check"></i>
          </div>
          <div class="flex flex-col">
            <span class="text-xs font-bold text-emerald-950">3. Test &amp; Confirm</span>
            <span class="text-[11px] text-gray-600 mt-0.5 leading-snug">When marked "Released", test the live application! Click "Confirm Fix" or "Reopen" if anything persists.</span>
          </div>
        </div>
      </div>

      <!-- ======================================================== -->
      <!-- SECTION 1: HOW IT WORKS (USER-CENTRIC EXPLANATION)        -->
      <!-- ======================================================== -->
      <div class="flex flex-col gap-3">
        <div class="flex items-center justify-between border-b border-gray-100 pb-2">
          <div>
            <h4 class="text-sm font-bold text-gray-900 m-0 flex items-center gap-2">
              <i class="fa-solid fa-route text-primary text-xs"></i>
              <span>How the Development Process Works</span>
            </h4>
            <p class="text-xs text-secondary-text m-0 mt-0.5">What happens from the moment you submit an issue or idea until it is live</p>
          </div>
          <span class="text-[11px] font-semibold text-primary bg-primary/10 px-2.5 py-0.5 rounded-full border border-primary/20">
            End-to-End Lifecycle
          </span>
        </div>

        <div class="grid grid-cols-1 @md:grid-cols-2 gap-3">
          
          <!-- Step 1 -->
          <div class="p-3.5 rounded-xl border border-gray-200 bg-white hover:border-gray-300 transition-colors shadow-2xs flex flex-col gap-1.5">
            <div class="flex items-center gap-2">
              <span class="w-6 h-6 rounded-full bg-gray-100 text-gray-800 font-bold text-xs flex items-center justify-center border border-gray-200">1</span>
              <div>
                <span class="font-bold text-xs text-gray-900 block">Report Bug or Request Feature</span>
                <span class="text-[10px] text-gray-500 font-medium">Initial Submission</span>
              </div>
              <span class="ml-auto text-[10px] font-semibold px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 border border-gray-200">Open</span>
            </div>
            <p class="text-[11px] text-gray-600 m-0 leading-relaxed mt-1">
              Drop a bubble pin onto the canvas or click <strong>Request Feature</strong> to describe what you need. Your task is created in <strong>Open</strong> status, visible to your workspace team.
            </p>
          </div>

          <!-- Step 2 -->
          <div class="p-3.5 rounded-xl border border-blue-200 bg-blue-50/20 hover:border-blue-300 transition-colors shadow-2xs flex flex-col gap-1.5">
            <div class="flex items-center gap-2">
              <span class="w-6 h-6 rounded-full bg-blue-100 text-blue-800 font-bold text-xs flex items-center justify-center border border-blue-200">2</span>
              <div>
                <span class="font-bold text-xs text-blue-950 block">Queue Acceptance</span>
                <span class="text-[10px] text-blue-700 font-medium">Admin Triage Gate</span>
              </div>
              <span class="ml-auto text-[10px] font-semibold px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200">Accepted</span>
            </div>
            <p class="text-[11px] text-gray-600 m-0 leading-relaxed mt-1">
              A Workspace Admin or Owner evaluates the request. Clicking <strong>Accept</strong> approves the task into the autonomous AI execution queue, ready to be worked on.
            </p>
          </div>

          <!-- Step 3 -->
          <div class="p-3.5 rounded-xl border border-indigo-200 bg-indigo-50/20 hover:border-indigo-300 transition-colors shadow-2xs flex flex-col gap-1.5">
            <div class="flex items-center gap-2">
              <span class="w-6 h-6 rounded-full bg-indigo-100 text-indigo-800 font-bold text-xs flex items-center justify-center border border-indigo-200">3</span>
              <div>
                <span class="font-bold text-xs text-indigo-950 block">Autonomous AI Builder</span>
                <span class="text-[10px] text-indigo-700 font-medium">Active Code Development</span>
              </div>
              <span class="ml-auto text-[10px] font-semibold px-2 py-0.5 rounded-full bg-blue-100 text-blue-800 border border-blue-200">In Progress</span>
            </div>
            <p class="text-[11px] text-gray-600 m-0 leading-relaxed mt-1">
              The AI Builder agent picks up the task, generates the required frontend and backend code, applies quality checks, and commits changes directly into the repository.
            </p>
          </div>

          <!-- Step 4 -->
          <div class="p-3.5 rounded-xl border border-purple-200 bg-purple-50/20 hover:border-purple-300 transition-colors shadow-2xs flex flex-col gap-1.5">
            <div class="flex items-center gap-2">
              <span class="w-6 h-6 rounded-full bg-purple-100 text-purple-800 font-bold text-xs flex items-center justify-center border border-purple-200">4</span>
              <div>
                <span class="font-bold text-xs text-purple-950 block">QA Verification Gate</span>
                <span class="text-[10px] text-purple-700 font-medium">Automated Tests &amp; Review</span>
              </div>
              <span class="ml-auto text-[10px] font-semibold px-2 py-0.5 rounded-full bg-purple-100 text-purple-800 border border-purple-200">In Review</span>
            </div>
            <p class="text-[11px] text-gray-600 m-0 leading-relaxed mt-1">
              Automated end-to-end (E2E) test suites execute in the test harness. Screen recordings and screenshots verify user journeys. QA confirms the feature works as intended.
            </p>
          </div>

          <!-- Step 5 -->
          <div class="p-3.5 rounded-xl border border-orange-200 bg-orange-50/20 hover:border-orange-300 transition-colors shadow-2xs flex flex-col gap-1.5">
            <div class="flex items-center gap-2">
              <span class="w-6 h-6 rounded-full bg-orange-100 text-orange-800 font-bold text-xs flex items-center justify-center border border-orange-200">5</span>
              <div>
                <span class="font-bold text-xs text-orange-950 block">Staging &amp; Deployment</span>
                <span class="text-[10px] text-orange-700 font-medium">Live Production Release</span>
              </div>
              <span class="ml-auto text-[10px] font-semibold px-2 py-0.5 rounded-full bg-sky-100 text-sky-800 border border-sky-200">Released</span>
            </div>
            <p class="text-[11px] text-gray-600 m-0 leading-relaxed mt-1">
              Passing QA moves the build through <strong>Ready for Release</strong> to <strong>Released</strong>. The updated microapplication is deployed live, and you are notified to try it out.
            </p>
          </div>

          <!-- Step 6 -->
          <div class="p-3.5 rounded-xl border border-emerald-200 bg-emerald-50/20 hover:border-emerald-300 transition-colors shadow-2xs flex flex-col gap-1.5">
            <div class="flex items-center gap-2">
              <span class="w-6 h-6 rounded-full bg-emerald-100 text-emerald-800 font-bold text-xs flex items-center justify-center border border-emerald-200">6</span>
              <div>
                <span class="font-bold text-xs text-emerald-950 block">Reporter Verification Loop</span>
                <span class="text-[10px] text-emerald-700 font-medium">Your Direct Confirmation</span>
              </div>
              <span class="ml-auto text-[10px] font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200">Confirmed</span>
            </div>
            <p class="text-[11px] text-gray-600 m-0 leading-relaxed mt-1">
              Test the solution on the live screen! If it works, click <strong>Confirm Fix</strong> to finish. If the problem still persists, click <strong>Reopen</strong> with details to send it back.
            </p>
          </div>

        </div>
      </div>

      <!-- ======================================================== -->
      <!-- SECTION 2: WORKFLOW DIAGRAM                               -->
      <!-- ======================================================== -->
      <div class="flex flex-col gap-2.5">
        <div class="flex items-center justify-between border-b border-gray-100 pb-2">
          <div>
            <h4 class="text-sm font-bold text-gray-900 m-0 flex items-center gap-2">
              <i class="fa-solid fa-diagram-project text-primary text-xs"></i>
              <span>Visual Task Workflow</span>
            </h4>
            <p class="text-xs text-secondary-text m-0 mt-0.5">Sequential lifecycle path from creation to completion or reopening</p>
          </div>
        </div>

        <div class="border border-gray-200 rounded-2xl bg-white p-6 shadow-2xs flex flex-col items-center">
          
          <!-- Open -->
          <div class="text-xs font-bold text-gray-700 px-4 py-1.5 rounded-md text-center bg-gray-50 border border-gray-200">
            Open
          </div>

          <!-- Down Arrow -->
          <div class="text-gray-400 my-1 text-xs">
            <i class="fa-solid fa-arrow-down"></i>
          </div>

          <!-- Accepted (Highlighted Blue Container) -->
          <div class="bg-blue-50 border border-blue-200 text-blue-900 font-bold text-xs px-10 py-2 rounded-lg text-center shadow-2xs">
            Accepted
          </div>

          <!-- Down Arrow -->
          <div class="text-gray-400 my-1 text-xs">
            <i class="fa-solid fa-arrow-down"></i>
          </div>

          <!-- In Progress -->
          <div class="text-xs font-semibold text-gray-800 px-4 py-1.5 rounded-md text-center bg-gray-50 border border-gray-200">
            In Progress
          </div>

          <!-- Down Arrow -->
          <div class="text-gray-400 my-1 text-xs">
            <i class="fa-solid fa-arrow-down"></i>
          </div>

          <!-- In Review -->
          <div class="text-xs font-semibold text-gray-800 px-4 py-1.5 rounded-md text-center bg-gray-50 border border-gray-200">
            In Review
          </div>

          <!-- Down Arrow -->
          <div class="text-gray-400 my-1 text-xs">
            <i class="fa-solid fa-arrow-down"></i>
          </div>

          <!-- Ready for Release -->
          <div class="text-xs font-semibold text-gray-800 px-4 py-1.5 rounded-md text-center bg-gray-50 border border-gray-200">
            Ready for Release
          </div>

          <!-- Down Arrow -->
          <div class="text-gray-400 my-1 text-xs">
            <i class="fa-solid fa-arrow-down"></i>
          </div>

          <!-- Released -->
          <div class="text-xs font-semibold text-gray-800 px-4 py-1.5 rounded-md text-center bg-gray-50 border border-gray-200">
            Released
          </div>

          <!-- Down Arrow -->
          <div class="text-gray-400 my-1 text-xs">
            <i class="fa-solid fa-arrow-down"></i>
          </div>

          <!-- TERMINAL BRANCHES (CONFIRMED vs REOPENED) -->
          <div class="w-full grid grid-cols-1 @sm:grid-cols-2 gap-4 mt-1">
            <!-- Confirmed Card (Green) -->
            <div class="border border-emerald-200 bg-emerald-50/40 rounded-xl p-4 flex flex-col items-center text-center shadow-2xs">
              <i class="fa-regular fa-circle-check text-emerald-600 text-xl"></i>
              <div class="font-bold text-gray-900 text-sm mt-1.5">Confirmed</div>
              <div class="text-xs text-gray-600 mt-0.5">Fix accepted by reporter</div>
              <span class="mt-2.5 text-[11px] font-semibold px-2.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 border border-emerald-300">
                Completed
              </span>
            </div>

            <!-- Reopened Card (Red) -->
            <div class="border border-rose-200 bg-rose-50/40 rounded-xl p-4 flex flex-col items-center text-center shadow-2xs">
              <i class="fa-solid fa-rotate-right text-rose-500 text-lg"></i>
              <div class="font-bold text-gray-900 text-sm mt-1.5">Reopened</div>
              <div class="text-xs text-rose-600 font-semibold mt-0.5">Reason required</div>
              <div class="text-xs text-gray-500 mt-0.5">Back to triage / development</div>
            </div>
          </div>

          <!-- Closed Administrative Termination Card -->
          <div class="w-full border border-gray-200 bg-gray-50/80 rounded-xl p-3.5 flex items-start gap-3 mt-4 text-left">
            <div class="w-8 h-8 rounded-lg bg-gray-200/80 flex items-center justify-center shrink-0 text-gray-600 text-sm mt-0.5">
              <i class="fa-regular fa-box-archive"></i>
            </div>
            <div class="flex-1 min-w-0">
              <div class="font-bold text-xs text-gray-900">Closed</div>
              <div class="text-[11px] text-gray-600 leading-relaxed mt-0.5">
                Administrative termination in 1 click (with an optional note). Can be reopened back to Open if needed; not counted as completed.
              </div>
            </div>
          </div>

        </div>
      </div>

      <!-- ======================================================== -->
      <!-- SECTION 3: TASK PIPELINE STAGES TABLE                    -->
      <!-- ======================================================== -->
      <div class="flex flex-col gap-2.5">
        <div class="flex items-center justify-between border-b border-gray-100 pb-2">
          <div>
            <h4 class="text-sm font-bold text-gray-900 m-0 flex items-center gap-2">
              <i class="fa-solid fa-table-list text-primary text-xs"></i>
              <span>Task Pipeline Stages</span>
            </h4>
            <p class="text-xs text-secondary-text m-0 mt-0.5">Reference table of all lifecycle statuses, their meaning, and ownership</p>
          </div>
          <span class="text-[11px] text-secondary-text font-medium">9 Canonical Stages</span>
        </div>

        <div class="border border-gray-200 rounded-xl overflow-hidden shadow-2xs bg-white">
          <table class="w-full text-left border-collapse text-xs">
            <thead>
              <tr class="border-b border-gray-200 bg-gray-50/75 text-gray-700 font-semibold">
                <th class="py-2.5 px-4 w-44">Status</th>
                <th class="py-2.5 px-4">Meaning</th>
                <th class="py-2.5 px-4 w-48">Who controls it</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-100">
              @for (item of stages; track item.status) {
                <tr class="hover:bg-gray-50/50 transition-colors">
                  <td class="py-2.5 px-4 whitespace-nowrap align-middle">
                    <span
                      class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border shadow-2xs"
                      [ngClass]="item.badgeClass"
                    >
                      {{ item.pillLabel }}
                    </span>
                  </td>
                  <td class="py-2.5 px-4 text-gray-700 align-middle">
                    {{ item.meaning }}
                  </td>
                  <td class="py-2.5 px-4 text-gray-600 font-medium whitespace-nowrap align-middle">
                    {{ item.whoControlsIt }}
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      </div>

    </div>
  </ng-container>

  <!-- MODAL FOOTER -->
  <ng-template #modalFooter>
    <div class="flex items-center justify-end w-full">
      <button
        type="button"
        (click)="close()"
        class="bg-primary hover:bg-hover-primary text-white font-bold px-4 py-2 rounded-xl text-xs shadow-sm transition cursor-pointer border-none"
        style="color: #ffffff !important;"
      >
        Got it, close guide
      </button>
    </div>
  </ng-template>
</nz-modal>
